import { Dispatch, FunctionComponent, SetStateAction, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { AutoComplete, AutoCompleteItem as ACItem } from "@/components/AutoComplete/AutoComplete";
import { Toggle } from "@/components/Toggle/Toggle";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { useCreateCompanyFeatureMutation } from "@/pages/Private/redux/feature/admin-feature.api";
import { useLazyGetCompaniesQuery } from "@/pages/Private/redux/companies/companies.api";

import { CreateCompanyFeature, CreateCompanyFeatureSchema } from "../schema/admin-feature";

interface AddOverrideModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const featureOptions: AutoCompleteItem[] = Object.values(FeaturesCodes).map((code) => ({
	id: code,
	title: code,
}));

export const AddOverrideModal: FunctionComponent<AddOverrideModalProps> = ({
	isOpen,
	setIsOpen,
}) => {
	const [createCompanyFeature, { isLoading, error }] = useCreateCompanyFeatureMutation();

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm<CreateCompanyFeature>({
		defaultValues: {
			feature: undefined as unknown as FeaturesCodes,
			companyId: undefined as unknown as number,
			active: true,
		},
		resolver: zodResolver(CreateCompanyFeatureSchema),
	});

	const [selectedCompanyName, setSelectedCompanyName] = useState("");
	const [triggerGetCompanies, { data: companiesData, isFetching: isCompaniesLoading }] =
		useLazyGetCompaniesQuery();
	const activeValue = watch("active");

	const companyOptions: ACItem[] = useMemo(
		() =>
			companiesData?.data.map((company) => ({
				id: company.id,
				title: company.name,
			})) || [],
		[companiesData]
	);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: CreateCompanyFeature) => {
		try {
			await createCompanyFeature(values).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText="Create Override"
			title="Add Company Feature Override"
		>
			<div className="flex flex-col gap-4">
				{formErrors.length > 0 && renderErrorMessages(formErrors)}
				{error && renderErrorMessages(getAllErrors(error, formErrors))}

				<Dropdown
					data={featureOptions}
					error={!!errors.feature}
					floating={false}
					handleSelect={(item) => {
						setValue("feature", item.id as FeaturesCodes, { shouldValidate: true });
					}}
					label="Feature Code"
				/>

				<AutoComplete
					data={companyOptions}
					error={!!errors.companyId}
					handleReset={() => {
						setSelectedCompanyName("");
						setValue("companyId", undefined as unknown as number, {
							shouldValidate: true,
						});
					}}
					handleSearch={(value) => {
						triggerGetCompanies({ page: 1, limit: 20, searchValue: value });
					}}
					handleSelect={(item) => {
						if (item) {
							setValue("companyId", item.id as number, { shouldValidate: true });
							setSelectedCompanyName(item.title);
						}
					}}
					isLoading={isCompaniesLoading}
					label="Company"
					placeholder="Search for a company..."
					selectedItem={selectedCompanyName}
				/>

				<div className="flex items-center gap-3">
					<span className="text-sm text-gray-700">Active</span>
					<Toggle
						isChecked={activeValue}
						onChange={() => setValue("active", !activeValue, { shouldValidate: true })}
					/>
				</div>
			</div>
		</Modal>
	);
};
